import {
  Literal,
  Number as RNumber,
  Record as RRecord,
  Static,
  Union,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

import {
  AnthropicModelLiteral,
  DeprecatedAnthropicModelLiteral,
} from "./anthropicTypes.js";
import { ChatCompletionsModelLiteral } from "./chatCompletionsTypes.js";
import {
  DeprecatedOpenAiChatModelLiteral,
  OpenAiChatModelLiteral,
} from "./openAiTypes.js";

/**
 * Structural exemptions are always exempt (e.g. BYOK, onboarding,
 * support user) and are never controlled by feature flags.
 */
const StructuralExemptBillingReasonLiteral = Union(
  /**
   * The first onboarding project is exempt from billing
   */
  Literal("ONBOARDING_PROJECT"),
  /**
   * Orgs with BYOK do not get charged for usage
   */
  Literal("BYOK"),
  /*
   * We don't bill directly for online evals, but we do track them for statistics
   * and in case we want to bill for them in the future.
   */
  Literal("ONLINE_EVAL"),
  /**
   * We don't bill directly for suggested prompt generation, but we do track them for statistics
   */
  Literal("SUGGESTED_PROMPT_GENERATION"),
  /*
   * We may use support users to debug agents in customer projects, and we don't
   * want to bill customers for that usage.
   */
  Literal("SUPPORT_USER"),
  /*
   * Don't bill for employee usage on customer orgs, so that users who are invited to a workspace (e.g. sales eng, customer eng) can
   * use the product for debugging and onboarding without incurring costs for customer
   */
  Literal("HEX_EMPLOYEE_ON_CUSTOMER_ORG"),
  /*
   * Thread summarization is not billed, but we track for statistics
   */
  Literal("THREAD_SUMMARIZATION"),
  /*
   * Confusion detection is not billed, but we track for statistics
   */
  Literal("CONFUSION_DETECTION"),
  /*
   * Thread topic discovery is not billed, but we track for statistics
   */
  Literal("THREAD_TOPIC_DISCOVERY"),
  /*
   * AI summaries for scheduled run notifications are not billed, but we track them for statistics
   */
  Literal("SCHEDULED_RUN_SUMMARY"),
  /*
   * Thread topic classification is not billed, but we track for statistics
   */
  Literal("THREAD_CLASSIFICATION"),
  /*
   * Memory extraction is not billed, but we track for statistics
   */
  Literal("MEMORY_EXTRACTION"),
  /*
   * Suggestion source grouping into issues is not billed, but we track for statistics
   */
  Literal("SUGGESTION_SOURCE_GROUPING"),
  /* Falling back is expensive since we lose caching, so we exempt threads to avoid annoying our customers */
  Literal("AGENT_PROVIDER_FALLBACK"),
  /*
   * Marks rows zeroed out when AI billing enforcement is activated for an org. Existing
   * usage in the current billing cycle is wiped (charges zeroed, reason set) so
   * the org starts enforcement with a clean slate while preserving the underlying
   * audit trail (token counts, model, run IDs).
   */
  Literal("PRE_ENFORCEMENT_RESET"),
  /* If the agent run results in an error, exempt the entirety of the run to avoid billing the user for the failed attempt */
  Literal("AGENT_RUN_ERROR"),
  /*
   * A platform admin has manually flagged this individual user as exempt from AI
   * credit usage (via the `aiCreditExempt` field on the user). Usage is still
   * tracked but never charged, and the credit-limit admission check is skipped.
   */
  Literal("MANUALLY_EXEMPTED_USER"),
);

/**
 * Feature exemptions are for product workflows that start exempt and eventually
 * get billed via the "ai-billing-feature-exemption" LaunchDarkly flag.
 * Callers always pass through an enum with a target exemption reason,
 * and the flag controls whether we thread it into usage tracking.
 */
export const FeatureExemptBillingReasonLiteral = Union(
  Literal("VISUALIZATION_SUBAGENT"),
  Literal("DATA_APP_SUBAGENT"),
  Literal("REVIEW_AGENT"),
  Literal("ARTIFACT_EXTRACTION_AGENT"),
  Literal("HERO_ANALYZER_AGENT"),
  Literal("HERO_AGGREGATOR_AGENT"),
  Literal("KNOWLEDGE_CURATION_AGENT"),
  Literal("SUBAGENT_WORK_SUMMARIZATION"),
);
export type FeatureExemptBillingReason = Static<
  typeof FeatureExemptBillingReasonLiteral
>;
export const FeatureExemptBillingReason = getNormalEnum(
  FeatureExemptBillingReasonLiteral,
);

export const ExemptBillingReasonLiteral = Union(
  ...StructuralExemptBillingReasonLiteral.alternatives,
  ...FeatureExemptBillingReasonLiteral.alternatives,
);
export type ExemptBillingReason = Static<typeof ExemptBillingReasonLiteral>;
export const ExemptBillingReason = getNormalEnum(ExemptBillingReasonLiteral);

export const RawBillingQuantityDetails = RRecord({
  inputTokens: RNumber,
  outputTokens: RNumber,
  cachedInputTokens: RNumber,
  cacheWrites: RNumber,
});
export type RawBillingQuantityDetails = Static<
  typeof RawBillingQuantityDetails
>;

/**
 * Billing-only identifiers for Anthropic fast mode. Fast mode is the same wire
 * model run with `speed: "fast"`, but Anthropic charges a per-model premium, so
 * we resolve and price it as a distinct {@link BillableModel}. These are
 * intentionally NOT part of `AnthropicModelLiteral`: they are never sent on the
 * wire and have no `AnthropicModelFeatureMap` entry.
 * @see https://platform.claude.com/docs/en/build-with-claude/fast-mode
 */
export const FastAnthropicBillableModelLiteral = Union(
  Literal("CLAUDE_4_8_OPUS_FAST"),
  Literal("CLAUDE_OPUS_5_FAST"),
);
export type FastAnthropicBillableModel = Static<
  typeof FastAnthropicBillableModelLiteral
>;
export const FastAnthropicBillableModel = getNormalEnum(
  FastAnthropicBillableModelLiteral,
);

/**
 * Billing-only identifier for GPT-5.6 Sol Fast mode. OpenAI uses the same wire
 * model with a premium service tier.
 */
export const FastOpenAiBillableModelLiteral = Union(Literal("GPT_56_FAST"));
export type FastOpenAiBillableModel = Static<
  typeof FastOpenAiBillableModelLiteral
>;
export const FastOpenAiBillableModel = getNormalEnum(
  FastOpenAiBillableModelLiteral,
);

// Note that if we want to use the same model from different providers, we can prefix the provider to the model name
/**
 * A billable model is a model we have defined a price for how much we charge customers to use
 */
export const BillableModel = Union(
  AnthropicModelLiteral,
  FastAnthropicBillableModelLiteral,
  FastOpenAiBillableModelLiteral,
  OpenAiChatModelLiteral,
  ChatCompletionsModelLiteral,
  // We need to keep around deprecated models here for historic
  // billing to be accurate since we store these in the DB
  DeprecatedAnthropicModelLiteral,
  DeprecatedOpenAiChatModelLiteral,
);
export type BillableModel = Static<typeof BillableModel>;
